import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EVALUATION_DIALOG } from "@/constants/testIds";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

// Formulario de evaluación de desempeño de la sección Evaluaciones (Talento). Los criterios
// se traen del backend (no se hardcodean aquí) para que siempre coincidan con lo que la API
// espera guardar — ver EVALUATION_CRITERIA en employees_router.py.
export function EvaluationDialog({ employee, open, onOpenChange, onSaved }) {
  const [criteria, setCriteria] = useState([]);
  const [scores, setScores] = useState({});
  const [periodLabel, setPeriodLabel] = useState("");
  const [evaluationDate, setEvaluationDate] = useState(todayIso());
  const [strengths, setStrengths] = useState("");
  const [areasToImprove, setAreasToImprove] = useState("");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/evaluations/criteria").then(({ data }) => setCriteria(data.criteria));
    setScores({});
    setPeriodLabel("");
    setEvaluationDate(todayIso());
    setStrengths("");
    setAreasToImprove("");
    setComments("");
  }, [open, employee?.id]);

  const allScored = criteria.length > 0 && criteria.every((c) => scores[c.key]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/employees/${employee.id}/evaluations`, {
        period_label: periodLabel,
        evaluation_date: evaluationDate,
        scores,
        strengths: strengths || null,
        areas_to_improve: areasToImprove || null,
        comments: comments || null,
      });
      toast.success("Evaluación guardada");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={EVALUATION_DIALOG.dialog} className="bg-bone max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Evaluación de desempeño{employee ? ` — ${employee.full_name}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Periodo evaluado</Label>
            <Input
              data-testid={EVALUATION_DIALOG.periodLabelInput}
              placeholder="Ej. Trimestre 1 2026, Mayo 2026..."
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
            />
          </div>
          <div>
            <Label>Fecha de evaluación</Label>
            <Input
              data-testid={EVALUATION_DIALOG.evaluationDateInput}
              type="date"
              value={evaluationDate}
              onChange={(e) => setEvaluationDate(e.target.value)}
            />
          </div>

          <div className="space-y-3">
            {criteria.map((c) => (
              <div key={c.key}>
                <Label className="block mb-1.5">{c.label}</Label>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`${EVALUATION_DIALOG.criterionScoreButton}-${c.key}-${n}`}
                      onClick={() => setScores((s) => ({ ...s, [c.key]: n }))}
                      className={`w-9 h-9 rounded-lg border text-sm font-medium transition-colors ${
                        scores[c.key] === n
                          ? "bg-petroleum text-white border-petroleum"
                          : "bg-white text-charcoal/60 border-petroleum/20 hover:bg-accent"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {criteria.length === 0 && <p className="text-xs text-charcoal/40">Cargando criterios...</p>}
          </div>

          <div>
            <Label>Fortalezas (opcional)</Label>
            <Textarea data-testid={EVALUATION_DIALOG.strengthsInput} value={strengths} onChange={(e) => setStrengths(e.target.value)} rows={2} />
          </div>
          <div>
            <Label>Áreas de oportunidad (opcional)</Label>
            <Textarea data-testid={EVALUATION_DIALOG.areasToImproveInput} value={areasToImprove} onChange={(e) => setAreasToImprove(e.target.value)} rows={2} />
          </div>
          <div>
            <Label>Comentarios generales (opcional)</Label>
            <Textarea data-testid={EVALUATION_DIALOG.commentsInput} value={comments} onChange={(e) => setComments(e.target.value)} rows={2} />
          </div>

          <Button
            data-testid={EVALUATION_DIALOG.submitButton}
            disabled={!periodLabel || !evaluationDate || !allScored || saving}
            onClick={handleSubmit}
            className="w-full bg-petroleum rounded-full"
          >
            Guardar evaluación
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
